import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useTableSort, sortIndicator } from '../lib/tableSort';
import './AiChatAdminPage.css';

interface AiDocument {
  id: number;
  title: string;
  sourceType: string;
  isPublic: boolean;
  chunkCount?: number;
  createdAt: string;
}

type SourceType = 'seah' | 'kisa' | 'general';

const SOURCE_LABELS: Record<SourceType, string> = {
  seah: '세아 내부 규정',
  kisa: 'KISA 가이드라인',
  general: '기타',
};

const AiChatAdminPage: React.FC = () => {
  const [documents, setDocuments] = useState<AiDocument[]>([]);
  const { sorted: sortedDocs, sortKey, sortDir, onSort } = useTableSort<AiDocument>(documents);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('seah');
  const [uploadMode, setUploadMode] = useState<'chunk' | 'chunk-public' | 'public-only'>('chunk-public');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const UPLOAD_MODES = [
    { value: 'chunk',        label: '청킹',         desc: 'AI Q&A 검색 전용 — 뷰어 미노출' },
    { value: 'chunk-public', label: '청킹 + 공개',  desc: 'AI Q&A 검색 + 규정 뷰어 공개' },
    { value: 'public-only',  label: '공개 only',    desc: '규정 뷰어 공개만 — 이미 청킹된 문서의 원본 파일 업로드 시 사용' },
  ] as const;

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await api.get<AiDocument[]>('/ai-chat/documents');
      setDocuments(res.data);
    } catch {
      setError('문서 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDocuments();
  }, []);

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) {
      setError('파일을 선택해주세요.');
      return;
    }

    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const invalid = Array.from(files).find(
      (f) => !allowed.includes(f.type) && !f.name.match(/\.(pdf|xlsx|xls)$/i),
    );
    if (invalid) {
      setError(`"${invalid.name}" — PDF 또는 Excel 파일만 업로드 가능합니다.`);
      return;
    }

    const isPublic = uploadMode !== 'chunk';
    const skipParsing = uploadMode === 'public-only';
    const modeLabel = UPLOAD_MODES.find((m) => m.value === uploadMode)?.label ?? '';

    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append('files', f));
    formData.append('sourceType', sourceType);
    formData.append('isPublic', String(isPublic));
    formData.append('skipParsing', String(skipParsing));

    try {
      setUploading(true);
      setError('');
      await api.post('/ai-chat/documents/upload', formData);
      const label = files.length === 1 ? `"${files[0].name}"` : `${files.length}개 파일`;
      setSuccess(`${label} 업로드 완료 (${modeLabel}).${skipParsing ? '' : ' 파싱 중입니다.'}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocuments();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '업로드에 실패했습니다.';
      setError(msg);
    } finally {
      setUploading(false);
      setTimeout(() => setSuccess(''), 4000);
    }
  };

  const handleToggleVisibility = async (doc: AiDocument) => {
    try {
      await api.patch(`/ai-chat/documents/${doc.id}/visibility`, { isPublic: !doc.isPublic });
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, isPublic: !d.isPublic } : d)),
      );
    } catch {
      setError('업데이트에 실패했습니다.');
    }
  };

  const handleDelete = async (doc: AiDocument) => {
    if (!window.confirm(`"${doc.title}" 문서를 삭제하시겠습니까?\n관련 chunk 데이터도 모두 삭제됩니다.`)) return;
    try {
      await api.delete(`/ai-chat/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      setSuccess(`"${doc.title}" 삭제 완료.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('삭제에 실패했습니다.');
    }
  };

  return (
    <div className="ai-admin-root">
      <div className="ai-admin-header">
        <h2>정보보호 AI 챗봇 — 문서 관리</h2>
        <p className="ai-admin-desc">
          업로드된 PDF/Excel 문서는 BM25 검색 인덱스로 파싱되어 AI Q&A에 활용됩니다.
        </p>
      </div>

      {/* 업로드 폼 */}
      <div className="ai-admin-card">
        <h3>문서 업로드</h3>
        <form className="upload-form" onSubmit={handleUpload}>
          <div className="form-row">
            <label className="form-label">파일 선택</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.xlsx,.xls"
              multiple
              className="form-file-input"
            />
            <span className="form-hint">PDF, Excel(.xlsx/.xls) 지원 — 여러 파일 동시 선택 가능</span>
          </div>
          <div className="form-row">
            <label className="form-label">출처 유형</label>
            <select
              className="form-select"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as SourceType)}
            >
              {(Object.keys(SOURCE_LABELS) as SourceType[]).map((key) => (
                <option key={key} value={key}>{SOURCE_LABELS[key]}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">업로드 방식</label>
            <div className="radio-group">
              {UPLOAD_MODES.map((mode) => (
                <label key={mode.value} className={`radio-option ${uploadMode === mode.value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="uploadMode"
                    value={mode.value}
                    checked={uploadMode === mode.value}
                    onChange={() => setUploadMode(mode.value)}
                  />
                  <span className="radio-label">{mode.label}</span>
                  <span className="radio-desc">{mode.desc}</span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="upload-btn" disabled={uploading}>
            {uploading ? '업로드 중...' : '업로드'}
          </button>
        </form>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
      </div>

      {/* 문서 목록 */}
      <div className="ai-admin-card">
        <h3>업로드된 문서 ({documents.length}개)</h3>
        {loading ? (
          <div className="loading-text">불러오는 중...</div>
        ) : documents.length === 0 ? (
          <div className="empty-text">업로드된 문서가 없습니다.</div>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th onClick={() => onSort('title')} style={{ cursor: 'pointer', userSelect: 'none' }}>문서명 {sortIndicator(sortKey, 'title', sortDir)}</th>
                <th onClick={() => onSort('sourceType')} style={{ cursor: 'pointer', userSelect: 'none' }}>출처 유형 {sortIndicator(sortKey, 'sourceType', sortDir)}</th>
                <th onClick={() => onSort('isPublic')} style={{ cursor: 'pointer', userSelect: 'none' }}>공개 여부 {sortIndicator(sortKey, 'isPublic', sortDir)}</th>
                <th onClick={() => onSort('createdAt')} style={{ cursor: 'pointer', userSelect: 'none' }}>업로드 일시 {sortIndicator(sortKey, 'createdAt', sortDir)}</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc) => (
                <tr key={doc.id}>
                  <td className="doc-title">{doc.title}</td>
                  <td>
                    <span className={`source-tag source-${doc.sourceType}`}>
                      {SOURCE_LABELS[doc.sourceType as SourceType] ?? doc.sourceType}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`visibility-btn ${doc.isPublic ? 'public' : 'private'}`}
                      onClick={() => handleToggleVisibility(doc)}
                      title="클릭하여 공개/비공개 전환"
                    >
                      {doc.isPublic ? '공개' : '비공개'}
                    </button>
                  </td>
                  <td className="doc-date">
                    {new Date(doc.createdAt).toLocaleDateString('ko-KR', {
                      year: 'numeric', month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(doc)}
                      title="문서 삭제"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AiChatAdminPage;
